import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  getMFLookthroughList, getMFLookthroughExposure, deleteMFLookthrough, uploadMFLookthrough,
  searchMFLookthrough, autoFetchMFLookthrough, autoFetchMFLookthroughAll,
  autoFetchMFHoldingsAll,
  fetchMFLookthroughFromUrl, getBookmarkletToken,
} from "../lib/api";
import {
  Layers, Upload, Trash2, ChevronRight, ChevronDown, Loader2, RefreshCw,
  TrendingUp, AlertTriangle, Search, Eye, EyeOff, Download, FileText, X,
  Sparkles, Zap, Wand2, Link2, Bookmark, Copy,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { toast } from "sonner";
import { getBackendUrl } from "../lib/backendUrl";

function fmtINR(v) {
  if (v == null || isNaN(v)) return "--";
  const a = Math.abs(v);
  if (a >= 1e7) return `₹${(v / 1e7).toFixed(2)}Cr`;
  if (a >= 1e5) return `₹${(v / 1e5).toFixed(2)}L`;
  return `₹${v.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}

const BUCKET_COLORS = {
  "Very High": "bg-red-500/15 text-red-300 border-red-500/30",
  "High": "bg-amber-500/15 text-amber-300 border-amber-500/30",
  "Medium": "bg-blue-500/15 text-blue-300 border-blue-500/30",
  "Low": "bg-white/5 text-white/60 border-white/10",
};

export default function FundsIntelPage() {
  const [data, setData] = useState(null);
  const [funds, setFunds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("stocks"); // stocks | funds | overlap | insights
  const [search, setSearch] = useState("");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [expandedStock, setExpandedStock] = useState(null);
  const [expandedFund, setExpandedFund] = useState(null);
  const [autoFetchingAll, setAutoFetchingAll] = useState(false);
  const [autoFetchingFund, setAutoFetchingFund] = useState(null); // fund_symbol currently fetching
  const [pickerFund, setPickerFund] = useState(null); // {fund_symbol, fund_name}
  const [urlFetchFund, setUrlFetchFund] = useState(null); // {fund_symbol, fund_name}
  const [bookmarkletOpen, setBookmarkletOpen] = useState(false);
  const [autoHoldingsResult, setAutoHoldingsResult] = useState(null);
  const [autoFetchingHoldings, setAutoFetchingHoldings] = useState(false);

  const load = useCallback(async () => {
    const [a, b] = await Promise.allSettled([getMFLookthroughExposure(), getMFLookthroughList()]);
    if (a.status === "fulfilled") setData(a.value.data);
    if (b.status === "fulfilled") setFunds(b.value.data?.funds || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const stocks = data?.top_stocks || [];
  const perFund = data?.per_fund || [];
  const insights = data?.insights || {};
  const s = data?.summary || {};

  const filteredStocks = useMemo(() => {
    if (!search) return stocks;
    const q = search.toLowerCase();
    return stocks.filter(x => x.stock?.toLowerCase().includes(q) || x.sector?.toLowerCase().includes(q));
  }, [stocks, search]);

  const overlapStocks = useMemo(
    () => stocks.filter(x => x.direct_value > 0 || x.total_exposure > 50000),
    [stocks],
  );

  const handleDeleteFund = async (fs) => {
    if (!window.confirm(`Remove look-through data for ${fs}?`)) return;
    await deleteMFLookthrough(fs);
    toast.success("Removed");
    load();
  };

  const handleAutoFetchOne = async (fund_symbol, fund_name) => {
    setAutoFetchingFund(fund_symbol);
    try {
      const { data } = await autoFetchMFLookthrough({ fund_symbol, fund_name });
      if (data?.success) {
        if (data.metadata_only) {
          toast.success(`${fund_symbol}: metadata enriched (${data.fund_house})`, { duration: 5000 });
        } else {
          toast.success(`${fund_symbol}: fetched ${data.holdings_count} stocks`);
        }
        load();
      } else {
        toast.error(data?.message || "No match — pick manually");
        setPickerFund({ fund_symbol, fund_name });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Auto-fetch failed");
    }
    setAutoFetchingFund(null);
  };

  const handleAutoFetchAll = async () => {
    if (!window.confirm("Enrich all funds via mfapi.in? This attaches fund house, category, and ISIN metadata. Underlying holdings still need CSV upload.")) return;
    setAutoFetchingAll(true);
    try {
      const { data } = await autoFetchMFLookthroughAll();
      toast.success(`Done: ${data.succeeded}/${data.attempted} funds enriched with mfapi.in metadata`, { duration: 5000 });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk auto-fetch failed");
    }
    setAutoFetchingAll(false);
  };

  const handleAutoFetchHoldings = async () => {
    if (!window.confirm("Fetch up to the top 10 stock holdings for every fund via Tickertape? If only five are publicly available, those five will be retained. Takes ~30s.")) return;
    setAutoFetchingHoldings(true);
    try {
      const { data } = await autoFetchMFHoldingsAll();
      setAutoHoldingsResult(data);
      toast.success(`Holdings populated: ${data.succeeded}/${data.attempted} funds`, { duration: 6000 });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk holdings fetch failed");
    }
    setAutoFetchingHoldings(false);
  };

  // Funds still missing ALL enrichment (no holdings AND no mfapi meta).
  // A fund enriched via mfapi still benefits from holdings CSV — but the
  // bulk button should only kick for genuinely un-touched funds.
  const fundsMissingCount = useMemo(
    () => perFund.filter((f) => !f.has_lookthrough && !f.mfapi_scheme_code).length,
    [perFund],
  );

  const fundsWithoutHoldings = useMemo(
    () => perFund.filter((f) => !f.has_lookthrough).length,
    [perFund],
  );

  if (loading) return <div className="flex items-center justify-center h-[60vh]"><Loader2 className="animate-spin text-[var(--brand)]" size={28} /></div>;

  return (
    <div className="p-4 md:p-6 max-w-[1600px] mx-auto space-y-5" data-testid="funds-intel-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Layers size={18} className="text-violet-400" />
            <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: "Chivo" }}>Funds Intel — MF Look-Through</h1>
          </div>
          <p className="text-sm text-[var(--text-muted)] mt-0.5">
            Your real stock exposure through {s.fund_count || 0} mutual funds · {s.unique_stocks || 0} unique stocks
            {fundsWithoutHoldings > 0 && (
              <span className="text-amber-400"> · {fundsWithoutHoldings} fund(s) awaiting holdings CSV</span>
            )}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={load} className="gap-1.5 border-white/10 text-xs" data-testid="funds-refresh-btn">
            <RefreshCw size={13} /> Refresh
          </Button>
          {fundsMissingCount > 0 && (
            <Button
              size="sm"
              onClick={handleAutoFetchAll}
              disabled={autoFetchingAll}
              className="gap-1.5 text-xs bg-violet-500 hover:bg-violet-500/90"
              data-testid="funds-auto-fetch-all-btn"
              title="Enrich fund metadata (house, category, ISIN) via mfapi.in"
            >
              {autoFetchingAll ? <Loader2 size={13} className="animate-spin" /> : <Wand2 size={13} />}
              Enrich All ({fundsMissingCount})
            </Button>
          )}
          {fundsWithoutHoldings > 0 && (
            <Button
              size="sm"
              onClick={handleAutoFetchHoldings}
              disabled={autoFetchingHoldings}
              className="gap-1.5 text-xs bg-cyan-500 hover:bg-cyan-500/90"
              data-testid="funds-auto-fetch-holdings-btn"
              title="Auto-fetch top holdings for every fund via Tickertape public sitemap"
            >
              {autoFetchingHoldings ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
              Auto-Fetch Holdings ({fundsWithoutHoldings})
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => setBookmarkletOpen(true)} className="gap-1.5 border-white/10 text-xs" data-testid="funds-bookmarklet-btn" title="Get Tickertape Premium bookmarklet">
            <Bookmark size={13} /> Bookmarklet
          </Button>
          <Button size="sm" onClick={() => setUploadOpen(true)} className="gap-1.5 text-xs bg-[var(--brand)] hover:bg-[var(--brand)]/90" data-testid="funds-upload-btn">
            <Upload size={13} /> Upload Holdings CSV
          </Button>
        </div>
      </div>

      {/* Auto-fetch holdings results */}
      {autoHoldingsResult && (
        <div className="bg-cyan-500/5 border border-cyan-500/20 rounded-lg p-4" data-testid="funds-auto-holdings-result">
          <div className="flex items-start justify-between gap-3 mb-2">
            <div className="flex items-center gap-2">
              <Sparkles size={16} className="text-cyan-400" />
              <div>
                <div className="text-sm font-semibold text-white">
                  Auto-Fetch Holdings: {autoHoldingsResult.succeeded}/{autoHoldingsResult.attempted} funds populated
                </div>
                <div className="text-[11px] text-[var(--text-muted)]">
                  Fetched up to the top 10 holdings from Tickertape
                </div>
              </div>
            </div>
            <button onClick={() => setAutoHoldingsResult(null)} className="text-[var(--text-muted)] hover:text-white" data-testid="funds-auto-holdings-dismiss">
              <X size={14} />
            </button>
          </div>
          {autoHoldingsResult.failed > 0 && (
            <details className="text-[11px] mt-2">
              <summary className="cursor-pointer text-amber-300 hover:text-amber-200">
                {autoHoldingsResult.failed} fund(s) failed — click to see
              </summary>
              <ul className="mt-2 space-y-1 pl-4 text-[10px] text-[var(--text-muted)]">
                {autoHoldingsResult.results?.filter(r => !r.success).map((r, i) => (
                  <li key={i}>
                    <span className="font-mono text-white">{r.fund_symbol}</span> — {r.error}
                    {r.error === "no_tickertape_match" && (
                      <span className="text-cyan-300/60"> (try Paste Fund Data URL)</span>
                    )}
                    {r.error === "no_holdings_scraped" && (
                      <span className="text-cyan-300/60"> (likely liquid/ETF fund — no equity)</span>
                    )}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}

      {/* Empty state */}
      {(!stocks || stocks.length === 0) && (
        <div className="bg-[var(--bg-surface)] border border-white/10 rounded-lg p-10 text-center" data-testid="funds-empty">
          <Layers size={36} className="text-[var(--text-muted)] mx-auto mb-3" />
          <h3 className="text-base font-semibold text-white mb-1">No look-through data yet</h3>
          <p className="text-sm text-[var(--text-muted)] mb-4 max-w-md mx-auto">
            One-click fetch underlying stock holdings for every fund via Tickertape's public sitemap, OR enrich metadata (fund house, category, ISIN) via <span className="text-violet-300 font-medium">mfapi.in</span>.
          </p>
          <div className="flex gap-2 justify-center flex-wrap">
            {(fundsWithoutHoldings || perFund.length) > 0 && (
              <Button size="sm" onClick={handleAutoFetchHoldings} disabled={autoFetchingHoldings}
                className="bg-cyan-500 hover:bg-cyan-500/90 gap-1.5"
                data-testid="funds-empty-auto-holdings-btn">
                {autoFetchingHoldings ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
                Auto-Fetch Holdings ({fundsWithoutHoldings || perFund.length} funds)
              </Button>
            )}
            {(fundsMissingCount || perFund.length) > 0 && (
              <Button size="sm" onClick={handleAutoFetchAll} disabled={autoFetchingAll}
                variant="outline"
                className="border-violet-500/30 text-violet-300 hover:bg-violet-500/10 gap-1.5"
                data-testid="funds-empty-auto-fetch-btn">
                {autoFetchingAll ? <Loader2 size={13} className="animate-spin" /> : <Wand2 size={13} />}
                Enrich Metadata ({fundsMissingCount || perFund.length})
              </Button>
            )}
            <a
              href={`${getBackendUrl()}/api/funds/lookthrough/template`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-white/5 border border-white/10 rounded hover:bg-white/10 text-white"
              data-testid="funds-template-btn"
            >
              <Download size={12} /> Sample Holdings CSV
            </a>
            <Button size="sm" variant="outline" onClick={() => setUploadOpen(true)} className="border-white/10 gap-1.5">
              <Upload size={13} /> Upload Holdings CSV
            </Button>
          </div>
        </div>
      )}

      {/* KPI grid */}
      {stocks.length > 0 && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <KPI label="MF Value" value={fmtINR(s.total_mf_value)} sub={`${s.fund_count} funds`} testId="kpi-mf-value" />
            <KPI label="MF P&L" value={fmtINR(s.total_mf_pnl)} sub={`${s.total_mf_pnl_pct >= 0 ? "+" : ""}${s.total_mf_pnl_pct}%`}
              positive={s.total_mf_pnl >= 0} testId="kpi-mf-pnl" />
            <KPI label="Unique Stocks" value={s.unique_stocks} sub={`Top: ${s.top_stock || "--"} (${s.top_stock_pct}%)`} testId="kpi-mf-stocks" />
            <KPI label="Top 10 Concentration" value={`${s.top10_concentration_pct}%`}
              sub={`${s.overlap_count} overlap with direct · ${s.hidden_only_count} hidden`} testId="kpi-mf-conc" />
          </div>

          {/* Tabs */}
          <div className="flex gap-1 bg-[var(--bg-surface)] border border-white/10 rounded-md p-0.5 self-start" data-testid="funds-tabs">
            {[
              { k: "stocks", l: `Top Stocks (${stocks.length})` },
              { k: "funds", l: `Funds (${perFund.length})` },
              { k: "overlap", l: `Direct + MF Overlap` },
              { k: "insights", l: "Insights" },
            ].map(({ k, l }) => (
              <button key={k} onClick={() => setTab(k)} data-testid={`funds-tab-${k}`}
                className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${tab === k ? "bg-[var(--brand)] text-white" : "text-[var(--text-secondary)] hover:text-white"}`}>
                {l}
              </button>
            ))}
          </div>

          {/* TOP STOCKS */}
          {tab === "stocks" && (
            <div className="space-y-3">
              <div className="relative max-w-sm">
                <Search size={13} className="absolute left-2.5 top-2.5 text-[var(--text-muted)]" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search stock or sector..."
                  className="pl-8 bg-[var(--bg-surface)] border-white/10 text-sm" data-testid="funds-search" />
              </div>
              <div className="bg-[var(--bg-surface)] border border-white/10 rounded-md overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      <TableHead className="w-8 pl-4"></TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-[var(--text-muted)] font-semibold">#</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-[var(--text-muted)] font-semibold">Stock</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-[var(--text-muted)] font-semibold">Sector</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-[var(--text-muted)] font-semibold">Cap</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-[var(--text-muted)] font-semibold text-right">MF Exposure</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-[var(--text-muted)] font-semibold text-right">% MF</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-[var(--text-muted)] font-semibold text-center">Funds</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-[var(--text-muted)] font-semibold text-right">Direct</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-[var(--text-muted)] font-semibold text-right pr-4">Combined</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredStocks.map((row, i) => (
                      <React.Fragment key={row.stock}>
                        <TableRow
                          className="border-b border-white/5 hover:bg-white/[0.02] cursor-pointer transition-colors"
                          onClick={() => setExpandedStock(expandedStock === row.stock ? null : row.stock)}
                          data-testid={`funds-stock-row-${row.stock}`}
                        >
                          <TableCell className="pl-4">
                            {expandedStock === row.stock ? <ChevronDown size={13} className="text-[var(--brand)]" /> : <ChevronRight size={13} className="text-[var(--text-muted)]" />}
                          </TableCell>
                          <TableCell className="font-mono text-xs text-[var(--text-muted)]">{i + 1}</TableCell>
                          <TableCell><span className="font-mono text-sm font-medium text-white">{row.stock}</span></TableCell>
                          <TableCell className="text-xs text-[var(--text-secondary)]">{row.sector || "--"}</TableCell>
                          <TableCell><span className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm border border-white/10 text-[var(--text-muted)]">{row.market_cap || "--"}</span></TableCell>
                          <TableCell className="text-right font-mono text-sm text-white">{fmtINR(row.total_exposure)}</TableCell>
                          <TableCell className="text-right font-mono text-xs text-[var(--text-secondary)]">{row.pct_of_mf}%</TableCell>
                          <TableCell className="text-center">
                            <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${row.fund_count >= 5 ? "bg-amber-500/15 text-amber-300" : "bg-white/5 text-[var(--text-muted)]"}`}>
                              {row.fund_count}
                            </span>
                          </TableCell>
                          <TableCell className="text-right font-mono text-xs text-[var(--text-secondary)]">{row.direct_value > 0 ? fmtINR(row.direct_value) : "—"}</TableCell>
                          <TableCell className="text-right pr-4 font-mono text-sm text-white">{fmtINR(row.combined_value)}</TableCell>
                        </TableRow>
                        {expandedStock === row.stock && (
                          <TableRow className="bg-[var(--bg-base)]" data-testid={`funds-stock-expand-${row.stock}`}>
                            <TableCell colSpan={10} className="px-4 py-3">
                              <div className="space-y-1.5 text-xs">
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] font-semibold">Contributing Funds</span>
                                  <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded border ${BUCKET_COLORS[row.bucket]}`}>{row.bucket} Exposure</span>
                                </div>
                                {row.funds.map((f) => (
                                  <div key={f.fund_symbol} className="flex items-center justify-between bg-[var(--bg-surface)] border border-white/5 rounded px-2.5 py-1.5">
                                    <div className="flex-1 min-w-0">
                                      <span className="text-white font-medium">{f.fund_name}</span>
                                      <span className="text-[var(--text-muted)] ml-2">· {f.allocation_pct}% of fund · my fund value {fmtINR(f.my_fund_value)}</span>
                                    </div>
                                    <div className="flex items-center gap-3">
                                      <span className="text-[var(--text-muted)] text-[10px]">{f.contribution_pct}% of stock exposure</span>
                                      <span className="font-mono text-white">{fmtINR(f.effective_value)}</span>
                                    </div>
                                  </div>
                                ))}
                                {row.direct_value > 0 && (
                                  <div className="flex items-center justify-between bg-emerald-500/5 border border-emerald-500/20 rounded px-2.5 py-1.5">
                                    <span className="text-emerald-300 font-medium">Direct holding</span>
                                    <span className="font-mono text-emerald-200">{fmtINR(row.direct_value)}</span>
                                  </div>
                                )}
                                <div className="flex items-center justify-between border-t border-white/10 pt-1.5 mt-2">
                                  <span className="text-white font-semibold">Total Combined Exposure</span>
                                  <span className="font-mono text-white font-bold">{fmtINR(row.combined_value)}</span>
                                </div>
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}

          {/* PER FUND */}
          {tab === "funds" && (
            <div className="space-y-2">
              {perFund.map((fund) => {
                const isOpen = expandedFund === fund.fund_symbol;
                return (
                  <div key={fund.fund_symbol} className="bg-[var(--bg-surface)] border border-white/10 rounded-md overflow-hidden" data-testid={`funds-fund-${fund.fund_symbol}`}>
                    <button onClick={() => setExpandedFund(isOpen ? null : fund.fund_symbol)}
                      className="w-full p-3 flex items-center justify-between hover:bg-white/[0.02] transition-colors text-left">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        {isOpen ? <ChevronDown size={13} className="text-[var(--brand)] flex-shrink-0" /> : <ChevronRight size={13} className="text-[var(--text-muted)] flex-shrink-0" />}
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-white font-medium truncate">{fund.fund_name}</div>
                          <div className="text-[10px] text-[var(--text-muted)] flex flex-wrap gap-x-2 gap-y-0.5">
                            <span className="font-mono">{fund.fund_symbol}</span>
                            <span>·</span>
                            <span>{fund.has_lookthrough ? `${fund.holdings.length} stocks` : "no holdings yet"}</span>
                            {fund.fund_house && <><span>·</span><span className="text-violet-300">{fund.fund_house}</span></>}
                            {fund.scheme_category && <><span>·</span><span>{fund.scheme_category}</span></>}
                            {fund.isin && <><span>·</span><span className="font-mono text-[9px] text-[var(--text-muted)]">{fund.isin}</span></>}
                            {fund.as_of && <><span>·</span><span>{fund.as_of}</span></>}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 text-xs">
                        <div className="text-right">
                          <div className="text-[9px] text-[var(--text-muted)] uppercase">Value</div>
                          <div className="font-mono text-white">{fmtINR(fund.my_value)}</div>
                        </div>
                        <div className="text-right">
                          <div className="text-[9px] text-[var(--text-muted)] uppercase">P&L</div>
                          <div className={`font-mono ${fund.my_pnl >= 0 ? "text-[var(--positive)]" : "text-[var(--negative)]"}`}>
                            {fund.my_pnl >= 0 ? "+" : ""}{fmtINR(fund.my_pnl)} ({fund.my_pnl_pct}%)
                          </div>
                        </div>
                        {fund.has_lookthrough ? (
                          <div onClick={(e) => { e.stopPropagation(); handleDeleteFund(fund.fund_symbol); }}
                            className="text-[var(--text-muted)] hover:text-red-400 p-1 rounded cursor-pointer" title="Remove look-through data">
                            <Trash2 size={12} />
                          </div>
                        ) : !fund.mfapi_scheme_code ? (
                          <div onClick={(e) => { e.stopPropagation(); handleAutoFetchOne(fund.fund_symbol, fund.fund_name); }}
                            className={`flex items-center gap-1 px-2 py-1 rounded text-[10px] font-semibold cursor-pointer border ${
                              autoFetchingFund === fund.fund_symbol
                                ? "border-violet-500/40 bg-violet-500/15 text-violet-200"
                                : "border-violet-500/30 bg-violet-500/10 text-violet-300 hover:bg-violet-500/20"
                            }`}
                            data-testid={`funds-fund-fetch-${fund.fund_symbol}`}
                            title="Enrich with mfapi.in metadata (fund house, category, ISIN)">
                            {autoFetchingFund === fund.fund_symbol
                              ? <Loader2 size={11} className="animate-spin" />
                              : <Wand2 size={11} />}
                            Enrich
                          </div>
                        ) : (
                          <span className="text-[9px] text-violet-300/70 font-medium italic" title="Enriched via mfapi.in">
                            meta ✓
                          </span>
                        )}
                      </div>
                    </button>
                    {isOpen && fund.has_lookthrough && (
                      <div className="border-t border-white/5 max-h-[400px] overflow-y-auto">
                        <Table>
                          <TableHeader>
                            <TableRow className="hover:bg-transparent">
                              <TableHead className="text-[10px] pl-4 uppercase text-[var(--text-muted)]">Stock</TableHead>
                              <TableHead className="text-[10px] uppercase text-[var(--text-muted)]">Sector</TableHead>
                              <TableHead className="text-[10px] uppercase text-[var(--text-muted)]">Cap</TableHead>
                              <TableHead className="text-[10px] uppercase text-[var(--text-muted)] text-right">Allocation</TableHead>
                              <TableHead className="text-[10px] uppercase text-[var(--text-muted)] text-right pr-4">My Effective Value</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {fund.holdings.slice(0, 50).map((h, i) => (
                              <TableRow key={`${h.stock}-${i}`} className="border-b border-white/5">
                                <TableCell className="pl-4 font-mono text-xs text-white">{h.stock}</TableCell>
                                <TableCell className="text-xs text-[var(--text-secondary)]">{h.sector}</TableCell>
                                <TableCell className="text-xs text-[var(--text-muted)]">{h.market_cap}</TableCell>
                                <TableCell className="text-right font-mono text-xs text-[var(--text-secondary)]">{h.allocation_pct}%</TableCell>
                                <TableCell className="text-right pr-4 font-mono text-xs text-white">{fmtINR(h.effective_value)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                        {fund.holdings.length > 50 && (
                          <div className="text-[10px] text-[var(--text-muted)] text-center py-2 italic">+ {fund.holdings.length - 50} more</div>
                        )}
                      </div>
                    )}
                    {isOpen && !fund.has_lookthrough && (
                      <div className="p-4 border-t border-white/5 text-xs space-y-2.5">
                        {fund.mfapi_scheme_code && (
                          <div className="text-violet-200 bg-violet-500/5 border border-violet-500/20 rounded px-3 py-2 flex items-start gap-2">
                            <Wand2 size={12} className="flex-shrink-0 mt-0.5 text-violet-300" />
                            <div>
                              <div className="font-medium">Metadata enriched via mfapi.in</div>
                              <div className="text-[10px] text-violet-200/70 mt-0.5">
                                Add underlying stock holdings using one of the options below.
                              </div>
                            </div>
                          </div>
                        )}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          <Button size="sm" onClick={() => setUrlFetchFund({ fund_symbol: fund.fund_symbol, fund_name: fund.fund_name })}
                            className="bg-cyan-500 hover:bg-cyan-500/90 gap-1.5 text-xs"
                            data-testid={`funds-fund-url-fetch-${fund.fund_symbol}`}>
                            <Link2 size={12} /> Paste Fund Data URL
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => setUploadOpen(true)}
                            className="border-white/10 text-[var(--text-secondary)] gap-1.5 text-xs">
                            <Upload size={12} /> Upload Holdings CSV
                          </Button>
                        </div>
                        {!fund.mfapi_scheme_code && (
                          <Button size="sm" onClick={() => handleAutoFetchOne(fund.fund_symbol, fund.fund_name)}
                            disabled={autoFetchingFund === fund.fund_symbol}
                            variant="outline"
                            className="border-violet-500/30 text-violet-300 hover:bg-violet-500/10 gap-1.5 text-xs w-full"
                            data-testid={`funds-fund-inline-fetch-${fund.fund_symbol}`}>
                            {autoFetchingFund === fund.fund_symbol
                              ? <Loader2 size={12} className="animate-spin" />
                              : <Wand2 size={12} />}
                            Enrich metadata via mfapi.in
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* OVERLAP */}
          {tab === "overlap" && (
            <div className="bg-[var(--bg-surface)] border border-white/10 rounded-md overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="text-xs uppercase tracking-wider text-[var(--text-muted)] pl-4">Stock</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-[var(--text-muted)] text-right">Direct</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-[var(--text-muted)] text-right">MF Exposure</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-[var(--text-muted)] text-right">Total</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-[var(--text-muted)] text-right pr-4">Risk</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {overlapStocks.slice(0, 50).map((row) => {
                    const risk = row.combined_value > 1000000 ? "high" : row.combined_value > 300000 ? "medium" : null;
                    return (
                      <TableRow key={row.stock} className="border-b border-white/5">
                        <TableCell className="pl-4">
                          <span className="font-mono text-sm text-white">{row.stock}</span>
                          {row.direct_value === 0 && <span className="ml-2 text-[9px] uppercase text-amber-400 bg-amber-500/10 border border-amber-500/20 px-1 rounded">hidden</span>}
                        </TableCell>
                        <TableCell className="text-right font-mono text-xs text-[var(--text-secondary)]">{row.direct_value > 0 ? fmtINR(row.direct_value) : "—"}</TableCell>
                        <TableCell className="text-right font-mono text-xs text-[var(--text-secondary)]">{fmtINR(row.total_exposure)}</TableCell>
                        <TableCell className="text-right font-mono text-sm text-white">{fmtINR(row.combined_value)}</TableCell>
                        <TableCell className="text-right pr-4">
                          {risk === "high" && <span className="text-[10px] px-1.5 py-0.5 bg-red-500/15 text-red-300 border border-red-500/30 rounded">⚠ High</span>}
                          {risk === "medium" && <span className="text-[10px] px-1.5 py-0.5 bg-amber-500/15 text-amber-300 border border-amber-500/30 rounded">Medium</span>}
                          {!risk && <span className="text-[10px] text-[var(--text-muted)]">—</span>}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}

          {/* INSIGHTS */}
          {tab === "insights" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <InsightCard
                title="Stocks in 5+ Funds"
                items={insights.stocks_in_5plus_funds || []}
                renderItem={(it) => (
                  <div className="flex items-center justify-between text-xs py-1">
                    <span className="font-mono text-white">{it.stock}</span>
                    <div className="flex items-center gap-3">
                      <span className="text-[10px] text-amber-300">in {it.fund_count} funds</span>
                      <span className="font-mono text-white">{fmtINR(it.total_exposure)}</span>
                    </div>
                  </div>
                )}
                empty="No stock appears in 5+ funds yet."
              />
              <InsightCard
                title="Top 10 Stocks by Effective Exposure"
                items={stocks.slice(0, 10)}
                renderItem={(it, i) => (
                  <div className="flex items-center justify-between text-xs py-1">
                    <span><span className="text-[var(--text-muted)] font-mono mr-2">{i + 1}.</span><span className="font-mono text-white">{it.stock}</span></span>
                    <span className="font-mono text-white">{fmtINR(it.total_exposure)}</span>
                  </div>
                )}
              />
              <InsightCard
                title="Most Duplicated Stock"
                singleValue={
                  insights.most_duplicated ? (
                    <div>
                      <div className="font-mono text-2xl text-white">{insights.most_duplicated.stock}</div>
                      <div className="text-xs text-[var(--text-muted)] mt-1">In {insights.most_duplicated.fund_count} funds · {fmtINR(insights.most_duplicated.total_exposure)} total exposure</div>
                    </div>
                  ) : "—"
                }
              />
              <InsightCard
                title="Biggest Hidden Exposure"
                singleValue={
                  insights.biggest_hidden_exposure ? (
                    <div>
                      <div className="font-mono text-2xl text-white">{insights.biggest_hidden_exposure.stock}</div>
                      <div className="text-xs text-[var(--text-muted)] mt-1">{fmtINR(insights.biggest_hidden_exposure.total_exposure)} via MFs · not in your direct portfolio · sector: {insights.biggest_hidden_exposure.sector}</div>
                    </div>
                  ) : "All MF stocks are also held directly."
                }
              />
            </div>
          )}
        </>
      )}

      {uploadOpen && <UploadCSVDialog onClose={() => setUploadOpen(false)} onSaved={load} />}
      {pickerFund && (
        <MFPickerDialog
          fund={pickerFund}
          onClose={() => setPickerFund(null)}
          onPicked={() => { setPickerFund(null); load(); }}
        />
      )}
      {urlFetchFund && (
        <UrlFetchDialog
          fund={urlFetchFund}
          onClose={() => setUrlFetchFund(null)}
          onSaved={async () => {
            await load();
            setUrlFetchFund(null);
          }}
        />
      )}
      {bookmarkletOpen && <BookmarkletDialog onClose={() => setBookmarkletOpen(false)} />}
    </div>
  );
}

function UrlFetchDialog({ fund, onClose, onSaved }) {
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [result, setResult] = useState(null);

  const finish = async () => {
    setRefreshing(true);
    try {
      await onSaved();
      toast.success("Funds Intel refreshed with the imported holdings");
    } catch (e) {
      toast.error("Holdings were saved, but refresh failed. Use the Refresh button.");
      setRefreshing(false);
    }
  };

  const submit = async () => {
    const sourceUrl = url.trim();
    if (!sourceUrl || (!sourceUrl.includes("tickertape.in") && !sourceUrl.includes("morningstar.in"))) {
      toast.error("Paste a valid Tickertape or Morningstar India fund URL");
      return;
    }
    setLoading(true);
    try {
      const { data } = await fetchMFLookthroughFromUrl({
        fund_symbol: fund.fund_symbol,
        fund_name: fund.fund_name,
            url: sourceUrl,
      });
      if (data?.success) {
        setResult(data);
        toast.success(`${data.holdings_count} top holdings fetched`);
      } else {
        toast.error(data?.message || "Fetch failed");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fetch failed");
    }
    setLoading(false);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[var(--bg-surface)] border-white/10 max-w-lg" data-testid="funds-url-dialog">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Link2 size={16} className="text-cyan-400" /> Fetch holdings from a fund data URL
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="bg-cyan-500/5 border border-cyan-500/20 rounded p-2.5 text-[11px] text-cyan-200 space-y-1.5">
            <div>Paste the Tickertape or Morningstar India page URL for <span className="font-mono text-cyan-100">{fund.fund_symbol}</span>.</div>
            <div className="text-cyan-300/60">
              We'll fetch up to the top 10 holdings and validate that the URL matches the selected fund before saving.
            </div>
          </div>
          {!result ? (
            <>
              <Input value={url} onChange={(e) => setUrl(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
                placeholder="https://www.morningstar.in/mutualfunds/.../detailed-portfolio.aspx"
                className="bg-[var(--bg-base)] border-white/10 text-xs font-mono"
                data-testid="funds-url-input" />
              <div className="text-[10px] text-[var(--text-muted)]">
                Supports Tickertape MF pages and Morningstar India detailed-portfolio pages.
              </div>
            </>
          ) : (
            <div className="bg-[var(--bg-base)] border border-white/10 rounded p-3 space-y-1.5">
              <div className="text-xs text-white font-medium">
                ✓ Fetched {result.holdings_count} holdings for {result.fund_name}
              </div>
              {result.top_3?.map((t, i) => (
                <div key={i} className="text-[11px] text-[var(--text-secondary)] flex justify-between">
                  <span>{t.name}</span>
                  <span className="font-mono">{t.weight}%</span>
                </div>
              ))}
              <div className="text-[10px] text-[var(--text-muted)] italic mt-2">{result.note}</div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose} className="border-white/10">Close</Button>
          {!result && (
            <Button size="sm" onClick={submit} disabled={loading}
              className="bg-cyan-500 hover:bg-cyan-500/90 gap-1.5 text-xs" data-testid="funds-url-submit">
              {loading ? <Loader2 size={13} className="animate-spin" /> : <Link2 size={13} />}
              Fetch Holdings
            </Button>
          )}
          {result && (
            <Button size="sm" onClick={finish} disabled={refreshing}
              className="bg-[var(--brand)] gap-1.5 text-xs">
              {refreshing && <Loader2 size={13} className="animate-spin" />}
              {refreshing ? "Refreshing…" : "Done"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BookmarkletDialog({ onClose }) {
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getBookmarkletToken().then((r) => { setToken(r.data.token); setLoading(false); })
      .catch(() => { toast.error("Failed to generate token"); setLoading(false); });
  }, []);

  const apiBase = `${getBackendUrl()}/api`;
  const bookmarkletCode = token ? buildBookmarklet(apiBase, token) : "";

  const copyCode = () => {
    navigator.clipboard.writeText(bookmarkletCode);
    toast.success("Bookmarklet code copied — create a new bookmark and paste as URL");
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[var(--bg-surface)] border-white/10 max-w-xl" data-testid="funds-bookmarklet-dialog">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Bookmark size={16} className="text-violet-400" /> Tickertape Premium Bookmarklet
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-xs text-[var(--text-secondary)]">
          <div className="bg-amber-500/10 border border-amber-500/30 rounded p-2.5 text-[11px] text-amber-200">
            💡 <b>Free user?</b> Use <b>"Paste Fund Data URL"</b> instead — it accepts Tickertape or Morningstar India and gets up to the top 10 holdings from
            Tickertape's public SEO data, which is more than the 3 stocks the UI shows to non-Pro users.
            The bookmarklet is mainly useful if you're on Tickertape Pro and want more than 5 holdings.
          </div>
          <div className="bg-violet-500/5 border border-violet-500/20 rounded p-3 space-y-1.5">
            <div className="text-violet-200 font-medium">How to install</div>
            <ol className="list-decimal list-inside space-y-1 text-[11px] text-violet-200/80 ml-2">
              <li>Show your browser's bookmarks bar (Ctrl/Cmd+Shift+B on Chrome)</li>
              <li><b>Drag</b> the purple "Capital360: Scrape MF" button below onto the bookmarks bar</li>
              <li>Visit any Tickertape MF page (like <span className="font-mono">tickertape.in/mutualfunds/...-M_XXXX</span>)</li>
              <li>Scroll to the "Current Holdings" table so it's rendered in the DOM</li>
              <li>Click the bookmark — it'll alert you when done</li>
            </ol>
            <div className="text-[10px] text-amber-300/80 pt-1">
              ⚠️ If drag-drop doesn't work (some browsers block it): click <b>Copy Code</b>, then add a bookmark manually and paste the code as the URL.
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-6"><Loader2 size={18} className="animate-spin text-[var(--brand)]" /></div>
          ) : !token ? (
            <div className="text-red-400 text-center py-4">Failed to get token</div>
          ) : (
            <>
              <div className="flex items-center justify-center py-4">
                <a href={bookmarkletCode}
                  onClick={(e) => e.preventDefault()}
                  draggable
                  onDragEnd={() => toast.success("Dropped! Check your bookmarks bar.")}
                  className="bg-violet-500 hover:bg-violet-600 text-white font-medium text-sm px-4 py-2 rounded cursor-grab active:cursor-grabbing inline-flex items-center gap-2 select-none"
                  data-testid="funds-bookmarklet-link">
                  <Bookmark size={14} /> Capital360: Scrape MF
                </a>
              </div>
              <div className="bg-[var(--bg-base)] border border-white/10 rounded p-2.5 font-mono text-[10px] break-all max-h-28 overflow-y-auto text-[var(--text-muted)]">
                {bookmarkletCode}
              </div>
              <Button size="sm" variant="outline" onClick={copyCode} className="w-full border-white/10 gap-1.5 text-xs">
                <Copy size={12} /> Copy Code
              </Button>
              <div className="text-[10px] text-[var(--text-muted)]">
                Token expires in 7 days. Regenerate anytime by reopening this dialog.
              </div>
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose} className="border-white/10">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Compact self-contained bookmarklet. Strategy:
//  1. Alert immediately (so user knows it ran)
//  2. Find "Current Holdings" container on Tickertape MF page
//  3. Walk DOM for all elements with "XX.XX%" patterns + their sibling name
//  4. POST scraped data to Capital360 ingest endpoint with Bearer token
// Resilient to DOM changes — pattern-based scraping, not class-name-based.
function buildBookmarklet(apiBase, token) {
  const src = `
    (function(){
      try{
        if(!location.host.includes('tickertape.in')){
          alert('Open a Tickertape MF page first (tickertape.in/mutualfunds/...)');
          return;
        }
        var sidMatch=location.pathname.match(/M_[A-Z0-9]+/i);
        if(!sidMatch){ alert('Not a Tickertape MF page (no M_XXXX SID in URL).'); return; }
        var sid=sidMatch[0].toUpperCase();
        var fundName=((document.querySelector('h1')||{}).innerText||'').trim();
        if(!fundName){ var h2=document.querySelector('h2'); if(h2) fundName=h2.innerText.replace(/Current Holdings/i,'').trim(); }

        // Locate the Current Holdings table container
        var h2s=Array.from(document.querySelectorAll('h1,h2,h3'));
        var hHead=h2s.find(function(x){ return /Current Holdings/i.test(x.innerText||''); });
        var cont=null;
        if(hHead){
          cont=hHead.closest('.table-container')||hHead.parentElement;
          while(cont && cont!==document.body){
            var pctCount=0;
            cont.querySelectorAll('*').forEach(function(e){
              if(e.childElementCount===0 && /^\\s*-?\\d+(\\.\\d+)?\\s*%\\s*$/.test((e.innerText||'').trim())) pctCount++;
            });
            if(pctCount>=3) break;
            cont=cont.parentElement;
          }
        }

        var holdings=[];
        var pctRe=/^\\s*(-?\\d+(?:\\.\\d+)?)\\s*%\\s*$/;
        var tickerRe=/^[A-Z][A-Z0-9&\\-]{1,14}$/;  // Uppercase ticker like HDFCBANK, BAJAJ-AUTO

        if(cont){
          // Each row has: {name, TICKER, holding-weight%, 3m-change%}
          // Find direct row elements — li, or div.table-row, or row-like
          var rows=cont.querySelectorAll('li, [class*="table-row"], [class*="Row"]');
          if(rows.length===0){
            // Fallback: any direct child div containing a %
            rows=Array.from(cont.children);
          }
          rows.forEach(function(r){
            if(!r.querySelectorAll) return;
            // Gather all leaf text nodes
            var leaves=[];
            r.querySelectorAll('*').forEach(function(e){
              if(e.childElementCount===0){
                var t=(e.innerText||'').trim();
                if(t) leaves.push(t);
              }
            });
            if(leaves.length<2) return;
            // Find name: first leaf that's a company name (has "Ltd/Limited/Inc" or a proper noun with lowercase letters and >5 chars)
            var name='';
            var ticker='';
            var pcts=[];
            leaves.forEach(function(t){
              var pm=t.match(pctRe);
              if(pm){ pcts.push(parseFloat(pm[1])); return; }
              if(!name){
                if(/(Ltd|Limited|Inc|Corp|Industries|Bank|Services|Company)\\b/i.test(t) && t.length<80){ name=t; return; }
                if(/[A-Z][a-z]/.test(t) && t.length>=5 && t.length<80 && !/%$/.test(t)){ name=t; return; }
              }
              if(!ticker && tickerRe.test(t) && t.length>=2 && t.length<=14){ ticker=t; }
            });
            // Holding weight = first NON-NEGATIVE pct, typically largest
            var pct=0;
            pcts.forEach(function(p){ if(p>pct && p<=100) pct=p; });
            if(name && pct>0){ holdings.push({name:name, stock:ticker||null, weight:pct}); }
          });
        }

        // Fallback B: FAQ-schema embedded in SSR HTML (top 5 always for SEO)
        if(holdings.length===0){
          var all=document.documentElement.outerHTML;
          var reFAQ=/([A-Z][A-Za-z0-9 \\-.&\\/()]+?(?:Ltd|Limited|Inc|Corp|Industries|Bank|Co|Company|Services))\\s*\\|\\s*(\\d+(?:\\.\\d+)?)\\s*%/g;
          var faqMap=new Map(), mF;
          while((mF=reFAQ.exec(all))!==null){
            var n=mF[1].trim(), p=parseFloat(mF[2]);
            if(!faqMap.has(n)) faqMap.set(n,p);
          }
          faqMap.forEach(function(p,n){ holdings.push({name:n, weight:p}); });
        }

        if(holdings.length===0){
          alert('No holdings found. Scroll to the "Current Holdings" section on this page and retry.\\n\\nIf you are not Tickertape Pro, only top 3 stocks are visible — use "Paste Tickertape URL" in Capital360 instead (gets 5 from SEO data).');
          return;
        }

        holdings.sort(function(a,b){ return b.weight-a.weight; });
        holdings=holdings.slice(0,30);

        var preview=holdings.slice(0,5).map(function(x){return '  '+x.name+(x.stock?' ['+x.stock+']':'')+' — '+x.weight+'%';}).join('\\n');
        var fs=prompt('Found '+holdings.length+' holdings for:\\n'+fundName+'\\n\\n'+preview+'\\n\\nCapital360 fund_symbol to save under?\\n(e.g. PPFAS, HDFCTOP100)');
        if(!fs){ return; }

        fetch('${apiBase}/funds/lookthrough/ingest-bookmarklet',{
          method:'POST',
          headers:{'Content-Type':'application/json','Authorization':'Bearer ${token}'},
          body:JSON.stringify({fund_symbol:fs.toUpperCase(),fund_name:fundName,tickertape_sid:sid,source_url:location.href,holdings:holdings})
        }).then(function(r){return r.json().then(function(d){return {ok:r.ok,d:d};});}).then(function(res){
          if(res.ok && res.d.success){
            alert('✓ Saved '+res.d.holdings_count+' holdings to '+res.d.fund_symbol+'\\n\\nTop 3:\\n'+res.d.top_3.map(function(x){return '  '+x.stock+' — '+x.weight+'%';}).join('\\n'));
          } else {
            alert('Ingest failed: '+(res.d.detail||res.d.message||JSON.stringify(res.d)));
          }
        }).catch(function(e){ alert('Network error: '+e.message); });
      }catch(e){ alert('Bookmarklet error: '+e.message); }
    })();
  `;
  return "javascript:" + encodeURIComponent(src.replace(/\s+/g, " ").trim());
}

function MFPickerDialog({ fund, onClose, onPicked }) {
  const [query, setQuery] = useState(fund.fund_name || fund.fund_symbol || "");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [submitting, setSubmitting] = useState(null); // sid being submitted
  const [hasSearched, setHasSearched] = useState(false);

  const runSearch = async () => {
    if (!query.trim() || query.trim().length < 2) {
      toast.error("Type at least 2 characters");
      return;
    }
    setSearching(true);
    setHasSearched(true);
    try {
      const { data } = await searchMFLookthrough(query.trim());
      setResults(data?.results || []);
      if ((data?.results || []).length === 0) {
        toast.error("Tickertape returned no matches");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Search failed");
    }
    setSearching(false);
  };

  // Auto-search on first open
  useEffect(() => { runSearch(); /* eslint-disable-line */ }, []);

  const choose = async (scheme_code) => {
    setSubmitting(scheme_code);
    try {
      const { data } = await autoFetchMFLookthrough({
        fund_symbol: fund.fund_symbol,
        fund_name: fund.fund_name,
        scheme_code,
      });
      if (data?.success) {
        toast.success(`${fund.fund_symbol}: ${data.metadata_only ? "metadata enriched" : `${data.holdings_count} stocks fetched`}`);
        onPicked();
      } else {
        toast.error(data?.message || "Failed to enrich");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Enrich failed");
    }
    setSubmitting(null);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[var(--bg-surface)] border-white/10 max-w-lg" data-testid="funds-picker-dialog">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Wand2 size={16} className="text-violet-400" /> Pick the right fund
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="bg-violet-500/5 border border-violet-500/20 rounded p-2.5 text-[11px] text-violet-200">
            Search mfapi.in for the exact scheme matching <span className="font-mono">{fund.fund_symbol}</span>.
            We'll attach its fund house, category, and ISIN.
          </div>
          <div className="flex gap-2">
            <Input value={query} onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") runSearch(); }}
              placeholder="e.g. parag parikh flexi"
              className="bg-[var(--bg-base)] border-white/10 text-sm" data-testid="funds-picker-query" />
            <Button size="sm" onClick={runSearch} disabled={searching} className="bg-violet-500 hover:bg-violet-500/90 gap-1.5 text-xs">
              {searching ? <Loader2 size={13} className="animate-spin" /> : <Search size={13} />} Search
            </Button>
          </div>
          <div className="max-h-[360px] overflow-y-auto space-y-1.5 pr-1">
            {results.length === 0 && hasSearched && !searching && (
              <div className="text-xs text-[var(--text-muted)] text-center py-6 italic">
                No matches. Try shorter keywords (e.g. "parag parikh" not "parag parikh flexi cap fund direct growth").
              </div>
            )}
            {results.map((r) => (
              <button key={r.scheme_code} onClick={() => choose(r.scheme_code)} disabled={submitting !== null}
                className={`w-full text-left p-2.5 rounded border transition-colors ${
                  submitting === r.scheme_code
                    ? "bg-violet-500/10 border-violet-500/40"
                    : "bg-[var(--bg-base)] border-white/10 hover:border-violet-500/40"
                }`}
                data-testid={`funds-picker-${r.scheme_code}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white font-medium truncate">{r.scheme_name}</div>
                    <div className="text-[10px] text-[var(--text-muted)]">code {r.scheme_code}</div>
                  </div>
                  {submitting === r.scheme_code
                    ? <Loader2 size={14} className="animate-spin text-violet-400 flex-shrink-0" />
                    : <ChevronRight size={14} className="text-[var(--text-muted)] flex-shrink-0" />}
                </div>
              </button>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose} className="border-white/10">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function KPI({ label, value, sub, positive, testId }) {
  const tone = positive === false ? "red" : positive === true ? "green" : "investment";
  return (
    <div data-tone={tone} className="semantic-card border rounded-md p-3" data-testid={testId}>
      <div className="text-[9px] tracking-[0.1em] uppercase text-[var(--text-muted)] font-semibold">{label}</div>
      <div className={`semantic-value font-mono text-xl font-medium tracking-tight mt-1 ${positive === false ? "text-[var(--negative)]" : positive === true ? "text-[var(--positive)]" : ""}`}>{value}</div>
      {sub && <div className="font-mono text-[10px] text-[var(--text-muted)] mt-0.5">{sub}</div>}
    </div>
  );
}

function InsightCard({ title, items, renderItem, singleValue, empty }) {
  return (
    <div className="bg-[var(--bg-surface)] border border-white/10 rounded-md p-4">
      <div className="text-[10px] uppercase tracking-[0.15em] text-[var(--text-muted)] font-semibold mb-3">{title}</div>
      {singleValue !== undefined ? (
        <div>{singleValue}</div>
      ) : items?.length ? (
        <div className="space-y-0">{items.map((it, i) => <React.Fragment key={i}>{renderItem(it, i)}</React.Fragment>)}</div>
      ) : (
        <div className="text-xs text-[var(--text-muted)] italic">{empty || "No data"}</div>
      )}
    </div>
  );
}

function UploadCSVDialog({ onClose, onSaved }) {
  const [file, setFile] = useState(null);
  const [fundSymbol, setFundSymbol] = useState("");
  const [fundName, setFundName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!file) return toast.error("Pick a CSV file");
    setSubmitting(true);
    const fd = new FormData();
    fd.append("file", file);
    if (fundSymbol) fd.append("fund_symbol", fundSymbol.trim().toUpperCase());
    if (fundName) fd.append("fund_name", fundName.trim());
    try {
      const { data } = await uploadMFLookthrough(fd);
      toast.success(`Uploaded look-through for ${data.funds_upserted} fund(s)`);
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    }
    setSubmitting(false);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[var(--bg-surface)] border-white/10 max-w-md" data-testid="funds-upload-dialog">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Upload size={16} className="text-violet-400" /> Upload MF Look-Through CSV
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="bg-violet-500/5 border border-violet-500/20 rounded p-2.5 text-[11px] text-violet-200 leading-relaxed">
            <strong>Two layouts accepted:</strong><br />
            <strong>Single fund:</strong> columns <code className="text-[10px] bg-black/30 px-1">stock, sector, market_cap, allocation_pct, isin</code> — pass fund_symbol below.<br />
            <strong>Multi-fund:</strong> add <code className="text-[10px] bg-black/30 px-1">fund_symbol, fund_name</code> columns.
            <a
              href={`${getBackendUrl()}/api/funds/lookthrough/template`}
              className="block mt-1.5 text-violet-300 underline"
              data-testid="funds-dialog-template-btn"
            >
              Download sample CSV →
            </a>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">CSV file *</label>
            <input type="file" accept=".csv" onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="mt-1 w-full text-xs text-white file:mr-2 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-[var(--brand)]/20 file:text-[var(--brand)] file:cursor-pointer cursor-pointer"
              data-testid="funds-csv-file" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">Fund Symbol (only for single-fund CSV)</label>
            <Input placeholder="e.g. HDFCTOP100" value={fundSymbol} onChange={(e) => setFundSymbol(e.target.value)}
              className="mt-1 bg-[var(--bg-base)] border-white/10 font-mono text-xs uppercase" data-testid="funds-csv-fund-symbol" />
            <p className="text-[10px] text-[var(--text-muted)] mt-0.5">Must match the fund's tradingsymbol in your holdings. Skip for multi-fund CSVs.</p>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">Fund Name (optional)</label>
            <Input placeholder="HDFC Top 100 Fund" value={fundName} onChange={(e) => setFundName(e.target.value)}
              className="mt-1 bg-[var(--bg-base)] border-white/10 text-sm" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose} className="border-white/10">Cancel</Button>
          <Button size="sm" onClick={submit} disabled={submitting || !file} className="bg-[var(--brand)] hover:bg-[var(--brand)]/90 gap-1.5" data-testid="funds-csv-submit">
            {submitting ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Upload
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
